import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import { verifyBusinessGeo } from "../../../shared/business-geo";
import {
  mapDicStayAreaListQueryParams,
  mapDicStayAreaMutationPayload,
  type DicStayAreaItemRaw,
  type DicStayAreaListPageRaw,
} from "../api/adapters";
import type {
  DicStayAreaGeoResolveRequest,
  DicStayAreaGeoResolveResult,
  DicStayAreaItem,
  DicStayAreaListPage,
  DicStayAreaListParams,
  DicStayAreaMutation,
} from "../types";

/** 查询住宿点分页列表。 */
export async function fetchDicStayAreaList(
  params: DicStayAreaListParams,
): Promise<DicStayAreaListPage> {
  const response = await httpClient.get<ApiEnvelope<DicStayAreaListPageRaw>>("/dic/stay-areas", {
    params: mapDicStayAreaListQueryParams(params),
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("住宿点列表接口返回了无效数据");
  }
  return mapDicStayAreaListPage(response.data.data);
}

/** 按主键读取住宿点。 */
export async function fetchDicStayArea(stayAreaId: string): Promise<DicStayAreaItem> {
  const response = await httpClient.get<ApiEnvelope<DicStayAreaItemRaw>>(
    `/dic/stay-areas/${stayAreaId}`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("住宿点详情接口返回了无效数据");
  }
  return mapDicStayAreaItem(response.data.data);
}

/** 创建住宿点。 */
export async function createDicStayArea(payload: DicStayAreaMutation): Promise<DicStayAreaItem> {
  const response = await httpClient.post<ApiEnvelope<DicStayAreaItemRaw>>(
    "/dic/stay-areas",
    mapDicStayAreaMutationPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("住宿点创建接口返回了无效数据");
  }
  return mapDicStayAreaItem(response.data.data);
}

/** 更新住宿点。 */
export async function updateDicStayArea(
  stayAreaId: string,
  payload: DicStayAreaMutation,
): Promise<DicStayAreaItem> {
  const response = await httpClient.put<ApiEnvelope<DicStayAreaItemRaw>>(
    `/dic/stay-areas/${stayAreaId}`,
    mapDicStayAreaMutationPayload(payload),
    { timeout: 180_000, "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("住宿点更新接口返回了无效数据");
  }
  return mapDicStayAreaItem(response.data.data);
}

/** 调用 GEO 识别接口。 */
export async function resolveGeoArea(
  payload: DicStayAreaGeoResolveRequest,
): Promise<DicStayAreaGeoResolveResult> {
  const result = await verifyBusinessGeo({
    province: payload.province,
    city: payload.city,
    district: payload.district,
    areaName: payload.areaName,
    scenario: "stay_area",
    contextName: [
      payload.contextName,
      payload.areaLevel ? `area_level=${payload.areaLevel}` : "",
      payload.township ? `township=${payload.township}` : "",
    ].filter(Boolean).join("; "),
    sourceAddress: payload.sourceAddress,
  });
  return {
    status: result.status,
    retryable: result.retryable,
    province: result.province,
    city: result.city,
    district: result.district,
    town: result.township ?? "",
    longitude: result.longitude,
    latitude: result.latitude,
    coordinateSystem: result.coordinateSystem,
    navigationAddress: result.amapStandardAddress,
    resolvedLevel: result.status === "confirmed" ? "area" : "unknown",
    confidenceScore: result.confidenceScore,
    needsReview: result.status !== "confirmed",
    message: result.correctionReason || result.conclusion,
    ambiguityNotes: result.llmConclusion,
  };
}
/** 映射住宿点分页响应。 */
function mapDicStayAreaListPage(raw: DicStayAreaListPageRaw): DicStayAreaListPage {
  return {
    items: (raw.items ?? []).map(mapDicStayAreaItem),
    page: raw.page,
    pageSize: raw.page_size,
    total: raw.total,
  };
}

/** 映射住宿点响应项。 */
function mapDicStayAreaItem(raw: DicStayAreaItemRaw): DicStayAreaItem {
  return {
    vectorSyncWarning: raw.vector_sync_warning ?? undefined,
    stayAreaId: raw.stay_area_id,
    stayAreaName: raw.stay_area_name,
    spellCode: raw.spell_code,
    areaLevel: raw.area_level,
    province: raw.province,
    city: raw.city,
    district: raw.district ?? "",
    township: raw.township ?? "",
    navigationAddress: raw.navigation_address,
    lng: raw.lng === null || raw.lng === undefined ? undefined : Number(raw.lng),
    lat: raw.lat === null || raw.lat === undefined ? undefined : Number(raw.lat),
    isDisabled: raw.is_disabled,
    isDisabledLabel: raw.is_disabled_label,
    isAiCreated: raw.is_ai_created,
    isAiCreatedLabel: raw.is_ai_created_label,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    remark: raw.remark,
  };
}
